import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';
import { AuthService, type ProfileData } from 'api/services/authService';
import { useAuthStore } from 'store/authStore';

interface Props {
  show: boolean;
  onHide: () => void;
}

export default function MyAccountModal({ show, onHide }: Props) {
  const { user, updateUser } = useAuthStore();
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show) return;
    setLoading(true);
    AuthService.getProfile()
      .then(setProfile)
      .catch(() => toast.error('Error al cargar el perfil'))
      .finally(() => setLoading(false));
  }, [show]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!profile) return;
    setProfile({ ...profile, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile) return;
    setSaving(true);
    try {
      await AuthService.updateProfile({
        name:           profile.name,
        firstLastName:  profile.firstLastName,
        secondLastName: profile.secondLastName ?? undefined,
        phone:          profile.phone ?? undefined,
        position:       profile.position ?? undefined,
      });
      updateUser({ name: profile.name, firstLastName: profile.firstLastName });
      toast.success('Perfil actualizado correctamente');
      onHide();
    } catch (err: any) {
      const msg = err?.response?.data?.metadata?.msg ?? 'Error al actualizar el perfil';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="ph ph-user-circle me-2" />
          Mi cuenta
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {loading ? (
            <p className="text-center text-muted py-3">Cargando...</p>
          ) : profile ? (
            <>
              <div className="mb-4 p-3 rounded d-flex align-items-center gap-3 border">
                <i className="ph ph-user-circle text-primary" style={{ fontSize: '3rem' }} />
                <div>
                  <h6 className="mb-0">{user?.name} {user?.firstLastName}</h6>
                  <small className="text-muted">{profile.email}</small>
                  {profile.position && <small className="text-muted d-block">{profile.position}</small>}
                </div>
              </div>
              <Row className="g-3">
                <Col md={4}>
                  <Form.Label>Nombre <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    name="name"
                    value={profile.name}
                    onChange={handleChange}
                    required
                  />
                </Col>
                <Col md={4}>
                  <Form.Label>Primer apellido <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    name="firstLastName"
                    value={profile.firstLastName}
                    onChange={handleChange}
                    required
                  />
                </Col>
                <Col md={4}>
                  <Form.Label>Segundo apellido</Form.Label>
                  <Form.Control
                    name="secondLastName"
                    value={profile.secondLastName ?? ''}
                    onChange={handleChange}
                  />
                </Col>
                <Col md={6}>
                  <Form.Label>Teléfono</Form.Label>
                  <Form.Control
                    name="phone"
                    value={profile.phone ?? ''}
                    onChange={handleChange}
                  />
                </Col>
                <Col md={6}>
                  <Form.Label>Puesto / Cargo</Form.Label>
                  <Form.Control
                    name="position"
                    value={profile.position ?? ''}
                    onChange={handleChange}
                  />
                </Col>
                <Col md={12}>
                  <Form.Label>Correo electrónico</Form.Label>
                  <Form.Control value={profile.email} disabled className="text-muted" />
                  <Form.Text className="text-muted">El correo no se puede modificar desde aquí.</Form.Text>
                </Col>
              </Row>
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={saving}>
            Cancelar
          </Button>
          <Button type="submit" variant="primary" disabled={saving || loading || !profile}>
            {saving
              ? <><span className="spinner-border spinner-border-sm me-2" />Guardando...</>
              : <><i className="ph ph-floppy-disk me-1" />Guardar</>}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
